import React, { useState, useEffect } from 'react';
import { Tabs, Empty, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import HistoryCell from './subviews/HistoryCell';
import type { HistoryOrderEntity } from 'types/dto/order';
import './OrderHistoryList.scss';
import { buildOrderHistoryDetailPath } from 'utils/pathBuilder';
import { fetchOrderHistory } from 'services/historyService';
import { isActiveStatus } from 'utils/commonUtils';
import { MemberProfile } from 'types/dto/profile';

const OrderHistoryList: React.FC = () => {
  const { t,i18n } = useTranslation('order_history');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [activeOrders, setActiveOrders] = useState<HistoryOrderEntity[]>([]);
  const [pastOrders, setPastOrders] = useState<HistoryOrderEntity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);
  const { profile } = useOutletContext<{ profile: MemberProfile | null }>();

  useEffect(() => {
    const initData = async () => {
      try {
        setIsLoading(true);
        setLoadError(false);

        const lang = i18n.language;
        const rawOrders: any[] = await fetchOrderHistory('history', lang, profile?.loyalty_account?.unique_id || '');

        const orders: HistoryOrderEntity[] = (rawOrders || []).map(item => ({
          ...item,
          oph_id: item.ophId,
          oph_regnum: item.ophRegNum,
          oph_colstrdtm: item.ophColStrDtm,
          oph_colenddtm: item.ophColEndDtm,
          oph_delstrdtm: item.ophDelStrDtm,
          oph_delenddtm: item.ophDelEndDtm,
          sts_nam: item.stsNam,
          glus_count: item.glusCount,
          oph_rmktocus: item.ophRmkToCus,
          oph_sts: item.ophSts,
        }));

        const active = orders.filter(order =>
          order.oph_sts && isActiveStatus(order.oph_sts)
        );

        const past = orders.filter(order =>
          order.oph_sts && !isActiveStatus(order.oph_sts)
        );

        setActiveOrders(active);
        setPastOrders(past);

      } catch (err) {
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    };

    initData();
  }, [i18n.language, profile?.loyalty_account?.unique_id, t]);

  const handleViewDetails = (orderId: number) => {
    navigate(buildOrderHistoryDetailPath(String(orderId)));
  };

  const renderOrderList = (orders: HistoryOrderEntity[]) => {
    if (orders.length === 0) {
      return <Empty description={t('noOrdersFound')} className="empty-state" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
      <div className="order-list">
        {orders.map((order: HistoryOrderEntity) => {
          if (!order.oph_id) return null;
          return (
            <HistoryCell
              key={order.oph_id}
              order={order}
              onViewDetails={() => handleViewDetails(order.oph_id as number)}
            />
          );
        })}
      </div>
    );
  };

  const tabItems = [
    {
      key: 'active',
      label: `${t('active')}`,
      children: renderOrderList(activeOrders),
    },
    {
      key: 'completed',
      label: `${t('completed')}`,
      children: renderOrderList(pastOrders),
    },
  ];

  return (
    <div className="order-history-list-container">
      <div className="order-history-list-content">
        <div className="list-section">
          <div className="vls-section-title">{t('orderHistory')}</div>

          {/* Show loading skeleton overlay while data is loading */}
          {isLoading && (
            <div className="skeleton-card">
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          )}

          {/* Show error alert if loading failed */}
          {!isLoading && loadError && (
            <div className="load-data-error">
              {tCommon('error.loadDataError')}
            </div>
          )}

          {!isLoading && !loadError && (
            <Tabs
              defaultActiveKey="active"
              items={tabItems}
              className="order-history-list-tabs"
              size="large"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHistoryList;
